import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import { Map, Loader2, Layers } from 'lucide-react';
import FloorPlanToolbar from './FloorPlanToolbar';
import FloorPlanLayerPanel from './FloorPlanLayerPanel';
import FloorPlanSheetPicker from './FloorPlanSheetPicker';
import FloorPlanChecklist from './FloorPlanChecklist';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanMeasurementsSidebar from './FloorPlanMeasurementsSidebar';
import FloorPlanCalibrationDialog from './FloorPlanCalibrationDialog';
import FloorPlanComparisonReport from './FloorPlanComparisonReport';
import FloorPlanBatchTakeoff from './FloorPlanBatchTakeoff';

export default function FloorPlanPanel({ projectId, onGoToDrawingSets }) {
  // ── Core state ────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('select');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [pinQuery, setPinQuery] = useState('');
  const [locating, setLocating] = useState(false);
  const locateMountedRef = useRef(true);
  useEffect(() => {
    locateMountedRef.current = true;
    return () => { locateMountedRef.current = false; };
  }, []);
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [autoPlotting, setAutoPlotting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [userAnnotations, setUserAnnotations] = useState([]);
  const [showSheetPicker, setShowSheetPicker] = useState(false);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [draggingAsset, setDraggingAsset] = useState(null);
  const svgRef = useRef(null);

  // ── Layer visibility ──────────────────────────────────────────────
  const [baseLayers, setBaseLayers] = useState({
    rooms: true, assets: true, pinnedAssets: true, annotations: true, labels: true,
  });
  const [overlayVisibility, setOverlayVisibility] = useState({});

  // ── Freehand drawing state ────────────────────────────────────────
  const [strokes, setStrokes] = useState([]);
  const [activeStroke, setActiveStroke] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#10B981');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const isDrawing = useRef(false);

  // ── Scale calibration & measurement state ──────────────────────────
  const [scaleCalibration, setScaleCalibration] = useState(null);
  const [calibPoints, setCalibPoints] = useState([]);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [measureResult, setMeasureResult] = useState(null);
  const [showCalibDialog, setShowCalibDialog] = useState(false);
  const [calibDistance, setCalibDistance] = useState('');
  const [calibUnit, setCalibUnit] = useState('ft');

  // ── Takeoff measurement state ──────────────────────────────────────
  const [takeoffMode, setTakeoffMode] = useState(null);
  const [takeoffPoints, setTakeoffPoints] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [savingMeasurement, setSavingMeasurement] = useState(false);
  const [measureLabel, setMeasureLabel] = useState('');
  const [measureMaterial, setMeasureMaterial] = useState('');
  const [measureTrade, setMeasureTrade] = useState('');
  const [detectingRooms, setDetectingRooms] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [aiTakeoffRunning, setAiTakeoffRunning] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null); // { id, points }
  const [editDragIdx, setEditDragIdx] = useState(null); // index of point being dragged
  const [showComparison, setShowComparison] = useState(false);
  const [showBatchTakeoff, setShowBatchTakeoff] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────
  const fetchFloorPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/floor-plan`);
      setData(res.data);
      if (res.data.layers?.annotations?.polygons) {
        setUserAnnotations(res.data.layers.annotations.polygons);
      }
      if (res.data.layers?.annotations?.strokes) {
        setStrokes(res.data.layers.annotations.strokes);
      }
      // Discipline overlays start hidden so pins only show on the active sheet;
      // the user toggles a discipline on to composite its pins onto the master.
      const vis = {};
      (res.data.overlay_sheets || []).forEach(os => { vis[os.id] = false; });
      setOverlayVisibility(vis);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load floor plan');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchFloorPlan(); }, [fetchFloorPlan]);

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/floor-plan/scale`)
      .then(r => setScaleCalibration(r.data))
      .catch(() => {});
    api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements`)
      .then(r => setMeasurements(r.data.measurements || []))
      .catch(() => {});
    api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-templates`)
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => {});
  }, [projectId]);

  // ── Keyboard shortcuts ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const k = e.key.toLowerCase();
      if (k === 'v') { setMode('select'); setDraggingAsset(null); }
      if (k === 'h') { setMode('pan'); setDraggingAsset(null); }
      if (k === 'd') { setMode('draw'); setDraggingAsset(null); }
      if (k === 'e') { setMode('edit'); setDraggingAsset(null); }
      if (k === 'x') { setMode('delete'); setDraggingAsset(null); }
      if (k === 'c') { setMode('calibrate'); setDraggingAsset(null); }
      if (k === 'm') { setMode('measure'); setDraggingAsset(null); }
      if (k === 'a' && !e.ctrlKey && !e.metaKey) { setMode('takeoff'); setTakeoffMode('area'); setTakeoffPoints([]); setDraggingAsset(null); }
      if (k === 'l') { setMode('takeoff'); setTakeoffMode('linear'); setTakeoffPoints([]); setDraggingAsset(null); }
      if (k === 'n') { setMode('takeoff'); setTakeoffMode('count'); setTakeoffPoints([]); setDraggingAsset(null); }
      if (k === 'escape') { setTakeoffPoints([]); setCalibPoints([]); setMeasurePoints([]); }
      if (k === '=' || k === '+') setZoom(z => Math.min(z * 1.2, 5));
      if (k === '-') setZoom(z => Math.max(z / 1.2, 0.2));
      if (k === 'z' && (e.ctrlKey || e.metaKey)) handleUndoStroke();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SVG point helper ──────────────────────────────────────────────
  const getSvgPoint = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  // ── Actions ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/annotations`, {
        polygons: userAnnotations, labels: [], strokes,
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch { setSaveStatus('error'); }
    finally { setSaving(false); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/closeout/projects/${projectId}/floor-plan/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'Floor_Plan_Handoff.pdf');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    } finally { setExporting(false); }
  };

  const handleChecklist = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/floor-plan/handoff-checklist`);
      setChecklist(res.data);
      setShowChecklist(true);
    } catch { alert('Failed to load checklist'); }
  };

  const handlePickSheet = async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/floor-plan/available-sheets`);
      setAvailableSheets(res.data.sheets || []);
      setShowSheetPicker(true);
    } catch { /* ignore */ }
  };

  const selectSheet = async (sheetId) => {
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/set-master`, { sheet_id: sheetId });
      setShowSheetPicker(false);
      fetchFloorPlan();
    } catch { alert('Failed to set floor plan'); }
  };

  const addOverlay = async (sheetId) => {
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/overlay-sheets`, { sheet_id: sheetId });
      setShowSheetPicker(false);
      fetchFloorPlan();
    } catch { alert('Failed to add overlay'); }
  };

  const removeOverlay = async (sheetId) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/floor-plan/overlay-sheets/${sheetId}`);
      fetchFloorPlan();
    } catch { alert('Failed to remove overlay'); }
  };

  // ── Asset pinning ─────────────────────────────────────────────────
  const handleSvgDrop = async (e) => {
    if (!draggingAsset || !svgRef.current) return;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    const d = data?.sheet?.dimensions
      || (data?.sheet?.width && data?.sheet?.height ? { width: data.sheet.width, height: data.sheet.height } : { width: 1600, height: 1100 });
    const fx = Math.max(0, Math.min(1, svgPt.x / d.width));
    const fy = Math.max(0, Math.min(1, svgPt.y / d.height));
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/pin-asset`, {
        asset_id: draggingAsset.asset_id, x: fx, y: fy,
        sheet_id: data?.sheet?.id,
      });
      setDraggingAsset(null);
      fetchFloorPlan();
    } catch { alert('Failed to pin asset'); }
  };

  const unpinAsset = async (assetId) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/floor-plan/pin-asset/${assetId}`);
      fetchFloorPlan();
    } catch { /* ignore */ }
  };

  const handleAutoPlot = async () => {
    setAutoPlotting(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/floor-plan/auto-plot`);
      const d = res.data;
      if (d.plotted > 0) fetchFloorPlan();
      alert(d.message || `Auto-plotted ${d.plotted} assets`);
    } catch (err) {
      alert('Auto-plot failed — ' + (err.response?.data?.detail || 'unknown error'));
    } finally { setAutoPlotting(false); }
  };

  // ── Freehand drawing handlers ─────────────────────────────────────
  const handleSvgMouseDown = (e) => {
    if (draggingAsset) return;
    if (mode === 'calibrate' || mode === 'measure') {
      handleCanvasClick(e);
      return;
    }
    if (mode === 'takeoff') {
      handleTakeoffClick(e);
      return;
    }
    if (mode !== 'draw') return;
    e.preventDefault();
    const pt = getSvgPoint(e);
    if (!pt) return;
    isDrawing.current = true;
    setActiveStroke({
      id: crypto.randomUUID(),
      points: [{ x: pt.x, y: pt.y }],
      color: strokeColor,
      width: strokeWidth,
    });
  };

  const handleSvgMouseMove = (e) => {
    if (!isDrawing.current || !activeStroke) return;
    const pt = getSvgPoint(e);
    if (!pt) return;
    setActiveStroke(prev => ({
      ...prev,
      points: [...prev.points, { x: pt.x, y: pt.y }],
    }));
  };

  const handleSvgMouseUp = () => {
    if (!isDrawing.current || !activeStroke) return;
    isDrawing.current = false;
    if (activeStroke.points.length > 1) {
      setStrokes(prev => [...prev, activeStroke]);
      setSaveStatus('unsaved');
    }
    setActiveStroke(null);
  };

  const handleDeleteStroke = (id) => {
    setStrokes(prev => prev.filter(s => s.id !== id));
    setSaveStatus('unsaved');
  };

  const handleUndoStroke = () => {
    setStrokes(prev => {
      if (prev.length === 0) return prev;
      setSaveStatus('unsaved');
      return prev.slice(0, -1);
    });
  };

  // ── Calibrate & Measure Handlers ──────────────────────────────────
  const handleCanvasClick = (e) => {
    const pt = getSvgPoint(e);
    if (!pt) return;

    if (mode === 'calibrate') {
      if (calibPoints.length === 0) {
        setCalibPoints([pt]);
      } else if (calibPoints.length === 1) {
        setCalibPoints([calibPoints[0], pt]);
        setShowCalibDialog(true);
      }
    }

    if (mode === 'measure') {
      if (measurePoints.length === 0) {
        setMeasurePoints([pt]);
        setMeasureResult(null);
      } else if (measurePoints.length === 1) {
        setMeasurePoints([measurePoints[0], pt]);
        if (scaleCalibration?.calibrated) {
          api.post(`/closeout/projects/${projectId}/floor-plan/measure`, {
            x1: measurePoints[0].x, y1: measurePoints[0].y,
            x2: pt.x, y2: pt.y,
          }).then(r => setMeasureResult(r.data))
            .catch(() => {});
        }
      } else {
        setMeasurePoints([pt]);
        setMeasureResult(null);
      }
    }
  };

  const handleCalibSubmit = async () => {
    if (!calibDistance || calibPoints.length < 2) return;
    try {
      const res = await api.put(`/closeout/projects/${projectId}/floor-plan/scale`, {
        point1_x: calibPoints[0].x, point1_y: calibPoints[0].y,
        point2_x: calibPoints[1].x, point2_y: calibPoints[1].y,
        known_distance: parseFloat(calibDistance),
        unit: calibUnit,
      });
      setScaleCalibration({ calibrated: true, ...res.data });
      const { toast } = await import('sonner');
      toast.success(`Scale set: ${res.data.known_distance} ${res.data.unit}`);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Calibration failed');
    }
    setShowCalibDialog(false);
    setCalibPoints([]);
    setCalibDistance('');
  };

  // ── Takeoff Measurement Handlers ──────────────────────────────────
  const handleTakeoffClick = (e) => {
    const pt = getSvgPoint(e);
    if (!pt) return;
    setTakeoffPoints(prev => [...prev, pt]);
  };

  const handleTakeoffDoubleClick = async () => {
    if (takeoffPoints.length < 2) return;
    await saveTakeoffMeasurement();
  };

  const saveTakeoffMeasurement = async () => {
    if (takeoffPoints.length < (takeoffMode === 'area' ? 3 : takeoffMode === 'linear' ? 2 : 1)) return;
    setSavingMeasurement(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements`, {
        type: takeoffMode,
        points: takeoffPoints,
        label: measureLabel,
        material: measureMaterial,
        trade: measureTrade,
      });
      setMeasurements(prev => [res.data, ...prev]);
      const { toast } = await import('sonner');
      toast.success(`${takeoffMode.charAt(0).toUpperCase() + takeoffMode.slice(1)} saved: ${res.data.real_value} ${res.data.unit}`);
      api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements`)
        .then(r => setMeasurements(r.data.measurements || []))
        .catch(() => {});
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to save measurement');
    } finally {
      setSavingMeasurement(false);
      setTakeoffPoints([]);
      setMeasureLabel('');
    }
  };

  const deleteMeasurement = async (id) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements/${id}`);
      setMeasurements(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDetectRooms = async () => {
    setDetectingRooms(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/floor-plan/detect-rooms`);
      const { toast } = await import('sonner');
      toast.success(res.data.message || `Detected ${res.data.rooms_detected} rooms`);
      fetchFloorPlan();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Room detection failed');
    } finally {
      setDetectingRooms(false);
    }
  };

  const handleSaveTemplate = async () => {
    const name = window.prompt('Template name:');
    if (!name) return;
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/takeoff-templates`, {
        name,
        trade: measureTrade,
        items: [{ type: takeoffMode || 'area', label: measureLabel, material: measureMaterial }],
      });
      const { toast } = await import('sonner');
      toast.success(`Template "${name}" saved`);
      api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-templates`)
        .then(r => setTemplates(r.data.templates || []))
        .catch(() => {});
    } catch (err) {
      console.error(err);
    }
  };

  const handleApplyTemplate = (template) => {
    if (template.items?.[0]) {
      const item = template.items[0];
      setMode('takeoff');
      setTakeoffMode(item.type || 'area');
      setMeasureLabel(item.label || '');
      setMeasureMaterial(item.material || '');
      setMeasureTrade(template.trade || '');
      setTakeoffPoints([]);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/floor-plan/takeoff-templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // ── AI Takeoff Agent ──────────────────────────────────────────────
  const handleAiTakeoff = async (trade) => {
    setAiTakeoffRunning(true);
    setShowMeasurements(true);
    try {
      const { toast } = await import('sonner');
      toast.info(`Running AI Takeoff for ${trade}...`);
      const res = await api.post(`/closeout/projects/${projectId}/floor-plan/ai-takeoff`, { trade });
      toast.success(res.data.message || `AI identified ${res.data.proposed_count} measurements`);
      // Refresh measurements list
      const mRes = await api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements`);
      setMeasurements(mRes.data.measurements || []);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'AI Takeoff failed');
    } finally {
      setAiTakeoffRunning(false);
    }
  };

  const handleReviewMeasurement = async (id, action) => {
    try {
      await api.patch(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements/${id}/review`, { action });
      setMeasurements(prev => action === 'reject'
        ? prev.filter(m => m.id !== id)
        : prev.map(m => m.id === id ? { ...m, status: 'accepted' } : m)
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleAcceptAllProposed = async (trade) => {
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/ai-takeoff/accept-all`, { trade });
      setMeasurements(prev => prev.map(m =>
        m.status === 'proposed' && (!trade || m.trade === trade) ? { ...m, status: 'accepted' } : m
      ));
      const { toast } = await import('sonner');
      toast.success('All proposed measurements accepted');
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectAllProposed = async (trade) => {
    try {
      await api.post(`/closeout/projects/${projectId}/floor-plan/ai-takeoff/reject-all`, { trade });
      setMeasurements(prev => prev.filter(m => !(m.status === 'proposed' && (!trade || m.trade === trade))));
      const { toast } = await import('sonner');
      toast.success('All proposed measurements rejected');
    } catch (err) {
      console.error(err);
    }
  };

  // ── Heat Map ───────────────────────────────────────────────────────
  const fetchHeatmap = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-heatmap?grid_size=20`);
      setHeatmapData(res.data);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    if (showHeatmap) fetchHeatmap();
  }, [showHeatmap, measurements.length, fetchHeatmap]);

  // ── Batch Takeoff Completion ──────────────────────────────────────
  const handleBatchTakeoffDone = async () => {
    setShowBatchTakeoff(false);
    const mRes = await api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-measurements`);
    setMeasurements(mRes.data.measurements || []);
    setShowMeasurements(true);
    const { toast } = await import('sonner');
    toast.success('Batch AI Takeoff complete — review proposed measurements');
  };

  // ── Editable Proposed Points ──────────────────────────────────────
  const startEditingMeasurement = (m) => {
    setEditingMeasurement({ id: m.id, points: m.points.map(p => ({ ...p })) });
    setEditDragIdx(null);
  };

  const handleEditPointMouseDown = (idx) => {
    setEditDragIdx(idx);
  };

  const handleEditPointMouseMove = (e) => {
    if (editDragIdx === null || !editingMeasurement || !svgRef.current) return;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    setEditingMeasurement(prev => {
      const newPts = [...prev.points];
      newPts[editDragIdx] = { x: svgPt.x, y: svgPt.y };
      return { ...prev, points: newPts };
    });
  };

  const handleEditPointMouseUp = () => {
    setEditDragIdx(null);
  };

  const saveEditedPoints = async () => {
    if (!editingMeasurement) return;
    try {
      const res = await api.put(
        `/closeout/projects/${projectId}/floor-plan/takeoff-measurements/${editingMeasurement.id}/points`,
        { points: editingMeasurement.points }
      );
      setMeasurements(prev => prev.map(m =>
        m.id === editingMeasurement.id
          ? { ...m, points: editingMeasurement.points, real_value: res.data.real_value, unit: res.data.unit }
          : m
      ));
      const { toast } = await import('sonner');
      toast.success(`Updated — ${res.data.real_value} ${res.data.unit}`);
    } catch (err) {
      console.error(err);
    }
    setEditingMeasurement(null);
    setEditDragIdx(null);
  };

  const cancelEditing = () => {
    setEditingMeasurement(null);
    setEditDragIdx(null);
  };

  // ── Loading / empty states ────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="floor-plan-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading floor plan...</span>
      </div>
    );
  }

  if (error || !data?.has_floor_plan) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center" data-testid="floor-plan-empty">
        <Map className="w-12 h-12 text-muted-foreground mb-3" />
        <p className="text-sm font-heading text-foreground mb-1">{error || 'No Floor Plan Linked'}</p>
        <p className="text-xs text-muted-foreground mb-4 max-w-md">
          Floor plans come from <span className="font-medium text-foreground/80">processed drawing sets</span>. Select an existing sheet, or process your uploaded drawings into sheets first.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={handlePickSheet} data-testid="link-floor-plan-btn"
            className="px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/15 transition-colors">
            Select Floor Plan
          </button>
          {onGoToDrawingSets && (
            <button onClick={onGoToDrawingSets} data-testid="go-to-drawing-sets-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-sm hover:bg-emerald-500/15 transition-colors">
              <Layers className="w-3.5 h-3.5" /> Process Drawings
            </button>
          )}
        </div>
        {showSheetPicker && <FloorPlanSheetPicker sheets={availableSheets} onSelect={selectSheet} onAddOverlay={addOverlay} onClose={() => setShowSheetPicker(false)} onProcessDrawings={onGoToDrawingSets} />}
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────
  const { sheet, layers: layerData, overlay_sheets, pinned_assets } = data;
  const imgUrl = sheet?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${sheet.image_url}` : null;
  const dims = sheet?.dimensions
    || (sheet?.width && sheet?.height ? { width: sheet.width, height: sheet.height } : { width: 1600, height: 1100 });
  const rooms = baseLayers.rooms ? (layerData.rooms || []) : [];
  const assets = baseLayers.assets ? (layerData.assets || []) : [];
  // Pins are shown for the master sheet plus any visible overlay (discipline) layers
  const visibleSheetIds = new Set([
    sheet?.id,
    ...(overlay_sheets || []).filter(o => overlayVisibility[o.id] !== false).map(o => o.id),
  ].filter(Boolean));
  const pinned = baseLayers.pinnedAssets
    ? (pinned_assets || []).filter(p => !p.sheet_id || visibleSheetIds.has(p.sheet_id))
    : [];
  const unmatched = layerData.unmatched_assets || [];
  const showAnnotations = baseLayers.annotations;

  // Click an asset in the side list: jump to & zoom its pin (making its layer
  // visible), or start a re-pin if it isn't placed yet.
  const locateAsset = (a) => {
    const pin = (pinned_assets || []).find(p => p.asset_id === a.asset_id);
    if (!pin) { setDraggingAsset(a); return; }
    if (pin.sheet_id && pin.sheet_id !== sheet?.id) {
      const ov = (overlay_sheets || []).find(o => o.id === pin.sheet_id);
      if (ov) {
        setOverlayVisibility(v => ({ ...v, [pin.sheet_id]: true }));
      } else {
        addOverlay(pin.sheet_id);  // add its sheet as a visible layer (non-destructive); click again to zoom
        return;
      }
    }
    const fx = pin.x <= 1 ? pin.x : pin.x / dims.width;
    const fy = pin.y <= 1 ? pin.y : pin.y / dims.height;
    // pan is in element (screen) px; convert the fractional sheet position using
    // the viewBox→container fit scale so the pin lands at the container center.
    const cont = svgRef.current?.parentElement;
    const rect = cont?.getBoundingClientRect();
    const W = rect?.width || dims.width;
    const H = rect?.height || dims.height;
    const fit = Math.min(W / dims.width, H / dims.height);
    setZoom(2.4);
    setPan({ x: (0.5 - fx) * dims.width * fit, y: (0.5 - fy) * dims.height * fit });
  };

  const pollLocateJob = async (jobId, label) => {
    const { toast } = await import('sonner');
    const started = Date.now();
    while (Date.now() - started < 360000) {
      await new Promise(r => setTimeout(r, 4000));
      if (!locateMountedRef.current) return;  // stop polling if unmounted
      let st;
      try {
        st = await api.get(`/closeout/projects/${projectId}/floor-plan/smart-locate-status/${jobId}`);
      } catch { continue; }
      if (!locateMountedRef.current) return;
      const d = st.data || {};
      if (d.located > 0) fetchFloorPlan();
      if (d.status === 'done') {
        toast.success(d.message || `${label} complete`);
        fetchFloorPlan();
        return;
      }
      if (d.status === 'error') {
        toast.error(d.message || `${label} failed`);
        return;
      }
    }
    toast.message(`${label} is still running in the background — pins will keep updating`);
  };

  const smartLocate = async () => {
    if (!sheet?.id) return;
    setLocating(true);
    try {
      const res = await api.post(
        `/closeout/projects/${projectId}/floor-plan/sheets/${sheet.id}/smart-locate`, {}
      );
      const { toast } = await import('sonner');
      if (res.data?.job_id) {
        toast.info(res.data.message || 'Smart Locate started');
        await pollLocateJob(res.data.job_id, 'Smart Locate');
      } else {
        toast.message(res.data?.message || 'Nothing to locate');
      }
    } catch {
      const { toast } = await import('sonner');
      toast.error('Smart Locate failed');
    } finally { setLocating(false); }
  };

  const smartLocateAll = async () => {
    setLocating(true);
    try {
      const res = await api.post(
        `/closeout/projects/${projectId}/floor-plan/smart-locate-all`, {}
      );
      const { toast } = await import('sonner');
      if (res.data?.job_id) {
        toast.info(res.data.message || 'Smart Locate started');
        await pollLocateJob(res.data.job_id, 'Smart Locate (all disciplines)');
      } else {
        toast.message(res.data?.message || 'No pins to locate yet');
      }
    } catch {
      const { toast } = await import('sonner');
      toast.error('Smart Locate (all disciplines) failed');
    } finally { setLocating(false); }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="floor-plan-panel">
      <FloorPlanToolbar
        mode={mode} setMode={setMode} zoom={zoom} setZoom={setZoom} setPan={setPan}
        saving={saving} saveStatus={saveStatus} handleSave={handleSave}
        exporting={exporting} handleExportPDF={handleExportPDF}
        handleChecklist={handleChecklist} handlePickSheet={handlePickSheet}
        strokeColor={strokeColor} setStrokeColor={setStrokeColor}
        strokeWidth={strokeWidth} setStrokeWidth={setStrokeWidth}
        onUndoStroke={handleUndoStroke} strokeCount={strokes.length}
        scaleCalibration={scaleCalibration} measureResult={measureResult}
        takeoffMode={takeoffMode} setTakeoffMode={setTakeoffMode}
        setTakeoffPoints={setTakeoffPoints} takeoffPoints={takeoffPoints}
        onFinishTakeoff={saveTakeoffMeasurement} savingMeasurement={savingMeasurement}
        showMeasurements={showMeasurements} setShowMeasurements={setShowMeasurements}
        measureLabel={measureLabel} setMeasureLabel={setMeasureLabel}
        measureMaterial={measureMaterial} setMeasureMaterial={setMeasureMaterial}
        measureTrade={measureTrade} setMeasureTrade={setMeasureTrade}
        onDetectRooms={handleDetectRooms} detectingRooms={detectingRooms}
        onAiTakeoff={handleAiTakeoff} aiTakeoffRunning={aiTakeoffRunning}
        onShowComparison={() => setShowComparison(true)}
        onBatchTakeoff={() => setShowBatchTakeoff(true)}
      />

      <div className="flex-1 flex overflow-hidden">
        <FloorPlanCanvas
          svgRef={svgRef} imgUrl={imgUrl} dims={dims} zoom={zoom} pan={pan} sheet={sheet} setZoom={setZoom} setPan={setPan}
          draggingAsset={draggingAsset} setDraggingAsset={setDraggingAsset} handleSvgDrop={handleSvgDrop}
          mode={mode} strokeColor={strokeColor} strokes={strokes} activeStroke={activeStroke}
          rooms={rooms} assets={assets} pinned={pinned}
          showAnnotations={showAnnotations} userAnnotations={userAnnotations}
          hoveredAsset={hoveredAsset} setHoveredAsset={setHoveredAsset}
          overlay_sheets={overlay_sheets} overlayVisibility={overlayVisibility}
          calibPoints={calibPoints} measurePoints={measurePoints}
          measureResult={measureResult} scaleCalibration={scaleCalibration}
          takeoffMode={takeoffMode} takeoffPoints={takeoffPoints} measurements={measurements}
          unmatched={unmatched} autoPlotting={autoPlotting} handleAutoPlot={handleAutoPlot}
          handleSvgMouseDown={handleSvgMouseDown} handleSvgMouseMove={handleSvgMouseMove}
          handleSvgMouseUp={handleSvgMouseUp} handleTakeoffDoubleClick={handleTakeoffDoubleClick}
          handleDeleteStroke={handleDeleteStroke}
          showLabels={baseLayers.labels}
          editingMeasurement={editingMeasurement}
          editDragIdx={editDragIdx}
          handleEditPointMouseDown={handleEditPointMouseDown}
          handleEditPointMouseMove={handleEditPointMouseMove}
          handleEditPointMouseUp={handleEditPointMouseUp}
          showHeatmap={showHeatmap}
          heatmapData={heatmapData}
        />

        <FloorPlanLayerPanel
          baseLayers={baseLayers} setBaseLayers={setBaseLayers} layerData={layerData}
          userAnnotations={userAnnotations} strokes={strokes}
          overlaySheets={overlay_sheets} overlayVisibility={overlayVisibility} setOverlayVisibility={setOverlayVisibility}
          removeOverlay={removeOverlay} onAddOverlay={handlePickSheet}
          unmatched={unmatched} draggingAsset={draggingAsset} setDraggingAsset={setDraggingAsset}
          pinnedAssets={pinned_assets} unpinAsset={unpinAsset}
          onLocate={locateAsset}
          pinQuery={pinQuery} setPinQuery={setPinQuery}
          onSmartLocate={smartLocate} onSmartLocateAll={smartLocateAll} locating={locating}
          hoveredAsset={hoveredAsset} setHoveredAsset={setHoveredAsset}
          showHeatmap={showHeatmap} setShowHeatmap={setShowHeatmap}
        />

        {showMeasurements && (
          <FloorPlanMeasurementsSidebar
            projectId={projectId} measurements={measurements} templates={templates}
            handleSaveTemplate={handleSaveTemplate} handleApplyTemplate={handleApplyTemplate}
            handleDeleteTemplate={handleDeleteTemplate} deleteMeasurement={deleteMeasurement}
            handleReviewMeasurement={handleReviewMeasurement}
            handleAcceptAllProposed={handleAcceptAllProposed}
            handleRejectAllProposed={handleRejectAllProposed}
            startEditingMeasurement={startEditingMeasurement}
            editingMeasurement={editingMeasurement}
            saveEditedPoints={saveEditedPoints}
            cancelEditing={cancelEditing}
          />
        )}
      </div>

      {/* Modals */}
      {showSheetPicker && (
        <FloorPlanSheetPicker
          sheets={availableSheets}
          currentMasterId={sheet?.id}
          overlayIds={(overlay_sheets || []).map(o => o.id)}
          onSelect={selectSheet}
          onAddOverlay={addOverlay}
          onClose={() => setShowSheetPicker(false)}
          onProcessDrawings={onGoToDrawingSets}
        />
      )}

      {showChecklist && checklist && (
        <FloorPlanChecklist data={checklist} onExport={handleExportPDF} exporting={exporting} onClose={() => setShowChecklist(false)} />
      )}

      {showCalibDialog && (
        <FloorPlanCalibrationDialog
          calibDistance={calibDistance} setCalibDistance={setCalibDistance}
          calibUnit={calibUnit} setCalibUnit={setCalibUnit}
          handleCalibSubmit={handleCalibSubmit}
          onClose={() => { setShowCalibDialog(false); setCalibPoints([]); }}
        />
      )}

      {showComparison && (
        <FloorPlanComparisonReport projectId={projectId} onClose={() => setShowComparison(false)} />
      )}

      {showBatchTakeoff && (
        <FloorPlanBatchTakeoff projectId={projectId} onClose={() => setShowBatchTakeoff(false)} onDone={handleBatchTakeoffDone} />
      )}
    </div>
  );
}
